import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';
import { SecureAPI } from '../config.js';

class VoiceRealtimeManager {
constructor(
    azureSpeechKey,
    azureSpeechRegion,
    selectedVoice,
    onTranscriptUpdate,
    onWaveformUpdate,
    onStatusUpdate,
    onToolCall,
    onAzureResponse,
    memoryService,
    userName,
    calendarData,
    detectMemoryQueryFn = null,
    generateSmartMemoryResponseFn = null,
    analyzeFollowUpFn = null
  ) {
    this.speechToken = null;
    this.tokenRegion = null;
    this.tokenExpiry = null;
    this.azureSpeechRegion = azureSpeechRegion || 'eastus2';
    
    this.memoriesShortTerm = [];
    this.memoriesLongTerm = [];
    this.goalsShortTerm = [];
    this.goalsMediumTerm = [];
    this.goalsLongTerm = [];
    this.lastMemoryLoad = null;
    this.speechConfig = null;
    this.recognizer = null;
    this.synthesizer = null;

    this.selectedVoice = selectedVoice || "en-US-AvaNeural";

    this.onTranscriptUpdate = onTranscriptUpdate;
    this.onWaveformUpdate = onWaveformUpdate;
    this.onStatusUpdate = onStatusUpdate;
    this.onToolCall = onToolCall;
    this.onAzureResponse = onAzureResponse;

    this.memoryService = memoryService;
    this.userName = userName;
    this.calendarData = calendarData;

    this.isConnected = false;
    this.isListening = false;
    this.isInitializing = false;
    this.isMuted = false;
    this.isPlayingAudio = false;
    this.isAISpeaking = false;
    this.isUserSpeaking = false;

    this.audioQueue = [];
    this.currentAudioElement = null;
    this.streamingAudioBuffer = [];

    this.audioContext = null;
    this.analyser = null;
    this.dataArray = null;
    this.animationFrameId = null;
    this.audioStream = null;

    this.audioSpeed = 1.05;
    this.audioPitch = 1.0;

    this.lastUserSpeechTime = 0;
    this.silenceTimer = null;
    this.messageHistory = [];
    this.lastToolCallTime = null;
    this.conversationActive = false;
    
    this.streamBuffer = '';
    this.ttsQueue = [];
    this.isTTSProcessing = false;
    this.frameSkipCounter = 0;
    this.currentWaveformData = null;

    this.detectMemoryQuery = detectMemoryQueryFn;
    this.generateSmartMemoryResponse = generateSmartMemoryResponseFn;
    this.analyzeFollowUp = analyzeFollowUpFn;
}
async getAuthToken() {
  try {
    console.log('🔐 Fetching Azure Speech token...');
    
    const tokenData = await SecureAPI.getAzureSpeechToken();
    
    if (!tokenData || !tokenData.token || !tokenData.region) {
      throw new Error('Invalid token response');
    }
    
    this.speechToken = tokenData.token;
    this.tokenRegion = tokenData.region;
    this.tokenExpiry = Date.now() + (9 * 60 * 1000);
    
    console.log('✅ Speech token obtained for region:', this.tokenRegion);
    
    return {
      token: this.speechToken,
      region: this.tokenRegion
    };
    
  } catch (error) {
    console.error('❌ Failed to get speech token:', error);
    throw new Error(`Speech authentication failed: ${error.message}`);
  }
}

async refreshTokenIfNeeded() {
  const now = Date.now();
  
  if (!this.speechToken || !this.tokenExpiry || now >= this.tokenExpiry) {
    console.log('🔄 Token expired or missing, refreshing...');
    await this.getAuthToken();
    
    if (this.speechConfig && this.speechToken) {
      console.log('🔄 Updating Speech SDK with new token...');
      this.speechConfig.authorizationToken = this.speechToken;
    }
    
    return true;
  }
  
  return false;
}
async initialize() {
  try {
    this.isInitializing = true;
    this.onStatusUpdate?.('One sec...');

    console.log('🎤 Initializing Azure Speech Recognition & TTS...');

    const { token, region } = await this.getAuthToken();
    
    this.onStatusUpdate?.('One sec...');

    this.speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(
      token,
      region
    );

    this.speechConfig.speechRecognitionLanguage = 'en-US';
    this.speechConfig.speechSynthesisVoiceName = this.selectedVoice;
    
    this.speechConfig.setProperty(
      SpeechSDK.PropertyId.SpeechServiceConnection_InitialSilenceTimeoutMs,
      '5000'
    );

    console.log(`✅ Speech config created with token auth`);
    console.log(`   Region: ${region}`);
    console.log(`   Voice: ${this.selectedVoice}`);

    this.synthesizer = new SpeechSDK.SpeechSynthesizer(this.speechConfig, null);

    this.onStatusUpdate?.('One sec...');

    this.audioStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      }
    });

    await this.setupWaveform();

    const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
    this.recognizer = new SpeechSDK.SpeechRecognizer(this.speechConfig, audioConfig);

    this.setupRecognitionHandlers();

    this.isConnected = true;
    this.isInitializing = false;

    console.log('✅ Azure Speech + TTS ready (token auth)');
    this.onStatusUpdate?.('Starting...');

    return true;
  } catch (error) {
    console.error('❌ Init failed:', error);
    this.isInitializing = false;
    this.onStatusUpdate?.('Failed to initialize');
    throw error;
  }
}


animateWaveform() {
  if (!this.isListening || !this.analyser || !this.dataArray) {
    return;
  }

  if (this.isMuted) {
    this.animationFrameId = requestAnimationFrame(() => this.animateWaveform());
    return;
  }

  this.analyser.getByteFrequencyData(this.dataArray);

  const points = [];
  const totalPoints = 60;
  const width = 600;
  const height = 50;
  const centerY = height / 2;
  const maxFreq = Math.max(...this.dataArray) / 255;

  for (let i = 0; i < totalPoints; i++) {
    const idx = Math.floor((i / totalPoints) * this.dataArray.length);
    const freq = this.dataArray[idx] / 255;
    const x = (i / totalPoints) * width;

    const waveHeight = Math.pow(freq, 1.5) * 15;
    const angle = (i / totalPoints) * Math.PI * 2;
    const wobble = Math.sin(angle) * waveHeight;

    const y = centerY - wobble;
    points.push(`${x},${y}`);
  }

  // ✅ STORE DATA INTERNALLY - DON'T CALL CALLBACK EVERY FRAME
  this.currentWaveformData = {
    points: points.join(' '),
    isActive: true,
    height: maxFreq * 50,
    frequencyData: Array.from(this.dataArray)
  };

  // ✅ ONLY CALL CALLBACK EVERY 2-3 FRAMES TO AVOID INFINITE UPDATES
  if (!this.frameSkipCounter) this.frameSkipCounter = 0;
  this.frameSkipCounter++;

  if (this.frameSkipCounter >= 3) {
    this.onWaveformUpdate?.(this.currentWaveformData);
    this.frameSkipCounter = 0;
  }

  this.animationFrameId = requestAnimationFrame(() => this.animateWaveform());
}

  async setupWaveform() {
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;

      const source = this.audioContext.createMediaStreamSource(this.audioStream);
      source.connect(this.analyser);

      this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);

      console.log('✅ Waveform ready');
    } catch (error) {
      console.error('⚠️ Waveform setup failed:', error);
    }
  }



async startListening(delayMs = 0) {
  try {
    await this.refreshTokenIfNeeded();

    if (!this.isConnected) {
      await this.initialize();
    }

    // Delay if provided
    if (delayMs > 0) {
      console.log(`⏳ Waiting ${delayMs}ms before listening...`);
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }

    console.log('🎤 Starting recognition');
    this.recognizer.startContinuousRecognitionAsync(
      () => {
        console.log('✅ Recognition active');
        this.isListening = true;
        this.animateWaveform();
      },
      (error) => {
        console.error('❌ Failed to start:', error);
        this.isListening = false;
      }
    );
  } catch (error) {
    console.error('❌ Error:', error);
    throw error;
  }
}

// ============================================
// STANDARD: Simple stopListening
// ============================================

stopListening() {
  if (this.recognizer) {
    console.log('🛑 Stopping recognition');
    this.recognizer.stopContinuousRecognitionAsync(
      () => {
        console.log('✅ Stopped');
        this.isListening = false;
      },
      (error) => {
        console.error('❌ Error:', error);
      }
    );
  }

  if (this.animationFrameId) {
    cancelAnimationFrame(this.animationFrameId);
    this.animationFrameId = null;
  }

  this.onWaveformUpdate?.({
    points: '',
    isActive: false,
    height: 0
  });
}


setupRecognitionHandlers() {
  // ✅ INDUSTRY STANDARD: Simple, proven thresholds
  const MIN_CONFIDENCE = 0.5; // Azure default
  
  let speechStartTime = null;
  let lastSpeechTime = null;

  // ✅ On recognizing (interim results)
  this.recognizer.recognizing = (s, e) => {
    if (e.result.reason === SpeechSDK.ResultReason.RecognizingSpeech) {
      const text = e.result.text;
      
      if (text && text.trim()) {
        // Track when speech started
        if (!speechStartTime) {
          speechStartTime = Date.now();
          console.log('🎤 Speech detected');
        }
        
        lastSpeechTime = Date.now();
        
        // Show interim text
        this.onTranscriptUpdate?.({
          type: 'user',
          text: text,
          isFinal: false,
          showInChat: true
        });
        
        // Interrupt AI if speaking
        if (this.isAISpeaking || this.isPlayingAudio) {
          console.log('⏸️ Stopping AI - user is speaking');
          this.interruptCurrentAudio();
          this.isAISpeaking = false;
          this.isPlayingAudio = false;
        }
      }
    }
  };

  // ✅ On recognized (final result)
  this.recognizer.recognized = (s, e) => {
    // ✅ CRITICAL FIX: Check reason FIRST
    if (!e.result) {
      console.warn('⚠️ No result in recognized event');
      return;
    }

    const reason = e.result.reason;
    
    if (reason === SpeechSDK.ResultReason.RecognizedSpeech) {
      const text = e.result.text;
      // ✅ FIX: confidence can be undefined, handle it
      const confidence = typeof e.result.confidence === 'number' 
        ? e.result.confidence 
        : 0.5; // Default to 0.5 if missing
      
      console.log(`✅ Recognized: "${text}" (${(confidence * 100).toFixed(0)}% confidence)`);
      
      // ✅ Check: text exists AND confidence is acceptable
      if (text && text.trim().length > 0 && confidence >= MIN_CONFIDENCE) {
        console.log('✅ ACCEPTING speech');
        
        // Stop AI if speaking
        if (this.isAISpeaking || this.isPlayingAudio) {
          console.log('⏸️ Stopping AI');
          this.interruptCurrentAudio();
          this.isAISpeaking = false;
          this.isPlayingAudio = false;
        }
        
        // Send final message
        this.onTranscriptUpdate?.({
          type: 'user',
          text: text,
          isFinal: true,
          showInChat: true
        });
        
        // Clear tool call history if recent
        if (this.lastToolCallTime && Date.now() - this.lastToolCallTime < 3000) {
          this.messageHistory = [];
          this.lastToolCallTime = null;
        }
        
        // Process the message
        this.scheduleResponseGeneration(text);
        
      } else if (text && text.trim().length === 0) {
        // Empty result
        console.log('⚠️ Empty speech result - likely silence');
      } else if (confidence < MIN_CONFIDENCE) {
        // Low confidence
        console.log(`⚠️ Low confidence (${(confidence * 100).toFixed(0)}%) - ignoring`);
      }
      
      // Reset tracking
      speechStartTime = null;
      lastSpeechTime = null;
      
    } 
    else if (reason === SpeechSDK.ResultReason.NoMatch) {
      console.log('⚠️ Speech not recognized - unclear or too quiet');
      speechStartTime = null;
      lastSpeechTime = null;
    } 
    else if (reason === SpeechSDK.ResultReason.Canceled) {
      const cancellation = e.result.cancellationDetails;
      console.error('❌ Recognition canceled:', cancellation?.errorDetails || 'Unknown error');
      speechStartTime = null;
      lastSpeechTime = null;
    }
    else {
      console.log('⚠️ Unexpected result reason:', reason);
      speechStartTime = null;
      lastSpeechTime = null;
    }
  };

  // ✅ Session handlers
  this.recognizer.sessionStarted = (s, e) => {
    console.log('🎤 Listening started');
    this.isListening = true;
  };

  this.recognizer.sessionStopped = (s, e) => {
    console.log('🎤 Listening stopped');
    this.isListening = false;
  };

  this.recognizer.canceled = (s, e) => {
    console.error('❌ Recognition canceled:', e.errorDetails);
    this.isListening = false;
  };
}

getAverageAudioLevel() {
  if (!this.analyser || !this.dataArray) {
    return 0;
  }

  // Get current frequency data
  this.analyser.getByteFrequencyData(this.dataArray);
  
  // Calculate average (voice typically in 85-255 range, background noise lower)
  let sum = 0;
  for (let i = 0; i < this.dataArray.length; i++) {
    sum += this.dataArray[i];
  }
  
  const average = sum / this.dataArray.length;
  
  // Normalize to 0-1 range (255 max)
  return average / 255;
}


  scheduleResponseGeneration(userMessage) {
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
    }

    if (this.isAISpeaking) {
      console.log('⏸️ AI still speaking, skipping...');
      return;
    }

    this.silenceTimer = setTimeout(() => {
      console.log('💬 Processing user message...');
      this.generateAndStreamResponse(userMessage);
    }, 800);
  }

  parseRelativeDate(dateString) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const lower = dateString.toLowerCase();
    
    if (lower === 'today') return today;
    
    if (lower === 'tomorrow') {
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      return tomorrow;
    }
    
    const nextDayMatch = lower.match(/next (monday|tuesday|wednesday|thursday|friday|saturday|sunday)/);
    if (nextDayMatch) {
      const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const targetDay = days.indexOf(nextDayMatch[1]);
      const currentDay = today.getDay();
      let daysToAdd = targetDay - currentDay;
      if (daysToAdd <= 0) daysToAdd += 7;
      
      const nextDate = new Date(today);
      nextDate.setDate(today.getDate() + daysToAdd);
      return nextDate;
    }
    
    const parsed = new Date(dateString);
    if (!isNaN(parsed.getTime())) return parsed;
    
    return today;
  }


async getAzureResponseStream(userMessage) {
  try {
    // ============================================
    // ✅ LOAD MEMORIES BEFORE EACH RESPONSE
    // ============================================
    await this.loadMemoriesForContext();

    // ============================================
    // ⚠️ CRITICAL: GET REAL-TIME INFO RIGHT NOW
    // Must be INSIDE this function, not at file load
    // ============================================
    const now = new Date();
    console.log('⏰ Calculating current time RIGHT NOW:', now.toISOString());
    
    // Get timezone info
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezoneOffset = -now.getTimezoneOffset() / 60;
    
    // Get detailed time components - FRESH CALCULATION
    const year = now.getFullYear();
    const month = now.toLocaleString('default', { month: 'long' });
    const monthNum = now.getMonth() + 1;
    const date = now.getDate();
    const dayOfWeek = now.toLocaleString('default', { weekday: 'long' });
    const hour = now.getHours();
    const minute = now.getMinutes();
    
    // Format for display
    const timeString = now.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    const fullDate = `${dayOfWeek}, ${month} ${date}, ${year}`;
    
    console.log(`✅ Time calculated: ${timeString} on ${fullDate}`);
    
    // Determine time of day
    let timeOfDay;
    if (hour >= 5 && hour < 12) timeOfDay = 'morning';
    else if (hour >= 12 && hour < 17) timeOfDay = 'afternoon';
    else if (hour >= 17 && hour < 21) timeOfDay = 'evening';
    else timeOfDay = 'night';

    // ============================================
    // BUILD MEMORY CONTEXT
    // ============================================
// ============================================
// ✅ LOAD & BUILD COMPLETE MEMORY CONTEXT
// ============================================
let memoryContext = '';
let memoryLoadSuccess = false;

// ✅ CRITICAL: Force reload memories on EVERY response
try {
  console.log('🧠 Reloading memories for THIS response...');
  await this.loadMemoriesForContext();
  memoryLoadSuccess = true;
  console.log('✅ Memories reloaded');
} catch (reloadError) {
  console.warn('⚠️ Memory reload failed:', reloadError);
}

if (memoryLoadSuccess) {
  // ✅ SHORT-TERM MEMORIES
  if (this.memoriesShortTerm && this.memoriesShortTerm.length > 0) {
    memoryContext += `\n\n⚡ URGENT MEMORIES (Next 7 days):\n`;
    memoryContext += `These are time-sensitive - ask about them:\n`;
    
    this.memoriesShortTerm.slice(0, 5).forEach(mem => {
      const timeRef = this.getNaturalTimeAgo ? this.getNaturalTimeAgo(mem.lastMentionedAt || mem.savedAt) : 'recently';
      memoryContext += `• ${mem.memory} (${timeRef})\n`;
    });
  }

  // ✅ LONG-TERM MEMORIES
  if (this.memoriesLongTerm && this.memoriesLongTerm.length > 0) {
    memoryContext += `\n\n📚 PERMANENT MEMORIES (Who they are):\n`;
    memoryContext += `Reference naturally when relevant:\n`;
    
    this.memoriesLongTerm.slice(0, 8).forEach(mem => {
      memoryContext += `• ${mem.memory}\n`;
    });
  }

  // ✅ GOALS
  if (this.goalsShortTerm && this.goalsShortTerm.length > 0) {
    memoryContext += `\n\n⚡ THIS WEEK'S GOALS:\n`;
    this.goalsShortTerm.slice(0, 3).forEach(goal => {
      memoryContext += `• ${goal.title}\n`;
    });
  }

  if (this.goalsMediumTerm && this.goalsMediumTerm.length > 0) {
    memoryContext += `\n\n📌 MEDIUM-TERM GOALS (1-3 months):\n`;
    this.goalsMediumTerm.slice(0, 3).forEach(goal => {
      memoryContext += `• ${goal.title}\n`;
    });
  }

  if (this.goalsLongTerm && this.goalsLongTerm.length > 0) {
    memoryContext += `\n\n🔄 LONG-TERM GOALS:\n`;
    this.goalsLongTerm.slice(0, 3).forEach(goal => {
      memoryContext += `• ${goal.title}\n`;
    });
  }

  memoryContext += `\n\n🧠 MEMORY INSTRUCTIONS:\n`;
  memoryContext += `✅ Reference memories naturally when user brings up related topics\n`;
  memoryContext += `✅ Ask about urgent memories (short-term) if relevant\n`;
  memoryContext += `✅ Use permanent memories for context when giving advice\n`;
  memoryContext += `❌ Don't list all memories unless asked\n`;
  memoryContext += `❌ Don't say "I remember you mentioned..." - sound natural\n`;
}
    // ✅ LONG-TERM MEMORIES (Permanent Knowledge)
    if (this.memoriesLongTerm && this.memoriesLongTerm.length > 0) {
      memoryContext += `\n\n📚 WHAT I KNOW ABOUT YOU (Permanent Knowledge):\n`;
      memoryContext += `These are core facts about the user that never expire:\n`;
      
      this.memoriesLongTerm.slice(0, 8).forEach(mem => {
        const importance = mem.importance ? `[${mem.importance.toUpperCase()}]` : '';
        memoryContext += `- ${mem.memory} ${importance}\n`;
      });
      
      memoryContext += `\n→ Reference these naturally when relevant to the conversation\n`;
      memoryContext += `→ Examples: "As a [job], you probably...", "With your [skill], you should..."\n`;
    }

    // ✅ GOALS (Short, Medium, Long term)
    if (this.goalsShortTerm && this.goalsShortTerm.length > 0) {
      memoryContext += `\n\n⚡ THIS WEEK'S GOALS (HIGH PRIORITY):\n`;
      this.goalsShortTerm.slice(0, 3).forEach(goal => {
        memoryContext += `- ${goal.title}\n`;
      });
      memoryContext += `→ Ask how they're progressing on these\n`;
    }

    if (this.goalsMediumTerm && this.goalsMediumTerm.length > 0) {
      memoryContext += `\n📌 MEDIUM-TERM GOALS (1-3 months):\n`;
      this.goalsMediumTerm.slice(0, 3).forEach(goal => {
        memoryContext += `- ${goal.title}\n`;
      });
    }

    if (this.goalsLongTerm && this.goalsLongTerm.length > 0) {
      memoryContext += `\n🔄 LONG-TERM GOALS (3+ months):\n`;
      this.goalsLongTerm.slice(0, 3).forEach(goal => {
        memoryContext += `- ${goal.title}\n`;
      });
    }

    // ✅ MEMORY BEHAVIOR RULES
    if (memoryContext) {
      memoryContext += `\n\n🧠 HOW TO USE THIS CONTEXT:\n`;
      memoryContext += `✅ DO:\n`;
      memoryContext += `- Reference memories naturally when user brings up related topics\n`;
      memoryContext += `- Ask follow-up questions about urgent memories\n`;
      memoryContext += `- Use their knowledge/skills when giving advice\n\n`;
      memoryContext += `❌ DON'T:\n`;
      memoryContext += `- Say "I remember you said..."\n`;
      memoryContext += `- Force memory references\n`;
      memoryContext += `- Repeat information they already know about themselves\n`;
      memoryContext += `- List all memories unless directly asked\n`;
    }

    // ============================================
    // BUILD SYSTEM PROMPT WITH REAL TIME
    // ============================================
    let systemPrompt = `You are Preptune, a voice assistant who talks like a real friend in natural conversation.

⏰ CURRENT TIME: ${timeString} on ${fullDate}
- Timezone: ${timezone}
- Time of Day: ${timeOfDay}
- Use this info naturally when user asks about time/date

---

## CORE PRINCIPLE
Be a genuine conversational partner—curious, engaged, and real. You're not just answering questions; you're having a dialogue where your perspective and personality matter.

---

## RESPONSE PACING & LENGTH

**Simple question = Short response:**
- 5-15 words, then stop
- User: "What time is it?"
- You: "It's ${timeString}"

**Normal conversation:**
- 20-40 words, natural stopping point
- User: "How do I study better?"
- You: "Okay so active recall is the game changer here. Like, instead of just reading your notes, quiz yourself. That's honestly what makes the biggest difference."

**Engaged explanation:**
- 40-80 words, you're genuinely into it
- User: "Tell me about your features"
- You: "Alright so I can help with a bunch of stuff. I remember what we talk about so you don't have to repeat yourself. I can also help you study with flashcards and quizzes, make podcasts about news or topics you care about, and keep track of your schedule. Oh and the cool thing is I adapt to how you're feeling, so if you're stressed I'll be more chill."

**NEVER exceed 100 words—break into follow-up turns if needed**

---

## NATURAL SPEECH PATTERNS

**Filler words (use 2-4 naturally per response):**
- Starting: "alright", "okay", "so", "well", "uhm", "hey"
- Mid-sentence: "like", "you know", "I mean", "uh", "haha"
- Thinking: "let me think", "hmm", "honestly", "to be honest"
- Emphasis: "actually", "basically", "for real"

**Real examples:**
- "Honestly I think that's, a risky move. What's your backup plan?"
- "Yeah so the thing is, like, you need to actually practice the material instead of just reading it."
- "Okay that's funny, haha, but real talk, have you actually tried it?"

---

## TONE & VOICE

- Casual and friendly, not corporate or robotic
- Use contractions: I'm, you're, it's, don't, can't, that's
- Sound interested in what they're saying
- Be honest about your perspective
- **NO EXCLAMATION MARKS** — use periods only
- Sound like you're actually thinking, not reading a script
- **AVOID slang like "lowkey," "heck yeah," "no cap," "bussin"** unless the user speaks that way first
- Stay appropriately casual—not overly slang-heavy

---

**Just be natural.**`;

    if (memoryContext) {
      systemPrompt += memoryContext;
      systemPrompt += `\n\nReference these naturally when relevant.`;
    }

    if (this.userName) {
      systemPrompt += `\n\nUSER'S NAME: ${this.userName}\nUse their name occasionally (1-2 times per conversation), not every sentence.`;
    }

    if (this.calendarData && Object.keys(this.calendarData).length > 0) {
      const todayKey = `${year}-${String(monthNum).padStart(2, '0')}-${String(date).padStart(2, '0')}`;
      
      systemPrompt += `\n\nCURRENT CALENDAR:\n`;
      
      if (this.calendarData[todayKey]?.length > 0) {
        systemPrompt += `Today (${fullDate}):\n`;
        this.calendarData[todayKey].forEach(event => {
          const status = event.completed ? '✓' : '○';
          const timeStr = event.time && event.time !== 'All day' ? ` at ${event.time}` : '';
          systemPrompt += `  ${status} ${event.text}${timeStr}\n`;
        });
      } else {
        systemPrompt += `Today: No events scheduled\n`;
      }
      
      systemPrompt += `\nWhen user asks about their schedule, reference these events naturally.`;
    }

    // ============================================
    // TOOLS ARRAY
    // ============================================
    const TOOLS = [
      {
        type: "function",
        function: {
          name: "schedule_event",
          description: "Add an event to the user's calendar",
          parameters: {
            type: "object",
            properties: {
              title: { type: "string", description: "Event title" },
              date: { type: "string", description: "Date (e.g., 'today', 'tomorrow', '2024-01-15')" },
              time: { type: "string", description: "Time in 24h format (e.g., '14:30') or 'All day'" },
              description: { type: "string", description: "Optional details" }
            },
            required: ["title", "date"]
          }
        }
      },
      {
        type: "function",
        function: {
          name: "get_schedule",
          description: "Get events for a specific date",
          parameters: {
            type: "object",
            properties: {
              date: { type: "string", description: "Date to query (e.g., 'today', 'tomorrow')" }
            },
            required: ["date"]
          }
        }
      },
      {
        type: "function",
        function: {
          name: "update_event",
          description: "Update an existing calendar event",
          parameters: {
            type: "object",
            properties: {
              eventTitle: { type: "string", description: "Current event title to find" },
              newTitle: { type: "string", description: "New title (optional)" },
              newDate: { type: "string", description: "New date (optional)" },
              newTime: { type: "string", description: "New time (optional)" },
              newDescription: { type: "string", description: "New description (optional)" }
            },
            required: ["eventTitle"]
          }
        }
      },
      {
        type: "function",
        function: {
          name: "delete_event",
          description: "Delete a calendar event",
          parameters: {
            type: "object",
            properties: {
              eventTitle: { type: "string", description: "Event title to delete" }
            },
            required: ["eventTitle"]
          }
        }
      }
    ];

    // ============================================
    // BUILD MESSAGES ARRAY
    // ============================================
    const messages = [
      { role: "system", content: systemPrompt },
      ...this.messageHistory.filter(msg => 
        msg.role === 'user' || (msg.role === 'assistant' && msg.content && !msg.content.includes('audio'))
      ),
      { role: "user", content: userMessage }
    ];

    console.log('📤 Streaming from Azure with TRUE streaming...');

    // ✅ USE TRUE STREAMING VERSION
    return await SecureAPI.callAzureOpenAIStreaming(messages, {
      tools: TOOLS,
      tool_choice: "auto",
      temperature: 0.7,
      max_tokens: 150
    });

  } catch (error) {
    console.error('❌ Azure stream error:', error);
    console.error('Error details:', {
      message: error.message,
      stack: error.stack,
      name: error.name
    });
    throw error;
  }
}

async generateAndStreamResponse(userMessage) {
  try {
    console.log('🎙️ Processing voice input...');

    // ============================================
    // STEP 1: Memory reference detection
    // ============================================
    if (this.memoryService?.isReady?.()) {
      try {
        const chatMessages = this.messageHistory.map((msg, idx) => ({
          id: Date.now() + idx,
          type: msg.role === 'user' ? 'user' : 'bot',
          content: msg.content || '',
          timestamp: new Date().toISOString()
        }));

        const referencedMemoryIds = await this.memoryService.detectAndUpdateMemoryReferences(
          userMessage,
          chatMessages
        );

        if (referencedMemoryIds?.length > 0) {
          console.log(`✅ Voice: Updated ${referencedMemoryIds.length} memory reference(s)`);
        }
      } catch (refError) {
        console.warn('⚠️ Memory reference detection error (voice):', refError);
      }
    }

    // ============================================
    // STEP 2: SMART MEMORY INTENT CHECK
    // Only skip if literally impossible to be a memory query.
    // Everything else goes to LLM with full context.
    // ============================================
    if (this.memoryService?.isReady?.()) {
      try {
        const msg = userMessage.trim();
        const charCount = msg.length;

        // Only skip if message is so short it physically can't be a memory query
        const isImpossiblyMemoryQuery = charCount < 8;

        if (isImpossiblyMemoryQuery) {
          console.log('⏭️ Message too short to be a memory query - skipping intent check');
        } else {
          // ✅ Pull conversation context for the LLM
          const lastAssistantMsg = [...this.messageHistory]
            .reverse()
            .find(m => m.role === 'assistant')?.content || '';

          const recentHistory = this.messageHistory
            .slice(-6)
            .map(m => `${m.role === 'user' ? 'User' : 'AI'}: ${m.content}`)
            .join('\n');

          console.log('🧠 LLM analyzing memory intent (voice)...');

          const intentAnalysis = await SecureAPI.callAzureOpenAI(
            [
              {
                role: 'system',
                content: `You are analyzing a voice conversation to decide if the user is asking to recall something from a past conversation or memory.

RECENT CONVERSATION:
${recentHistory}

LAST AI MESSAGE: "${lastAssistantMsg.substring(0, 150)}"
USER NOW SAYS: "${msg}"

Use ALL of this context to decide. Examples of what to consider:

✅ IS a memory query:
- "do you remember when I told you about my exam?"
- "what did we talk about last week?"
- "didn't I mention I had a soccer game?"
- "remind me what I said about that job"
- "what was that thing I told you about my sister?"
- "I think I mentioned something about this before"

❌ NOT a memory query:
- User is directly answering a question the AI just asked
- User is reacting to what AI said ("yeah that makes sense", "oh interesting")
- User is sharing new information about themselves
- User is asking for help with something new
- User is continuing a story they were already telling

CRITICAL: In voice conversations punctuation is often missing from speech-to-text. Do not rely on question marks. Read intent from meaning, not punctuation.

Return ONLY valid JSON:
{
  "isMemoryQuery": true or false,
  "confidence": 0.0-1.0,
  "reason": "brief explanation of why",
  "wantsDetails": true or false,
  "wantsReconstruction": true or false
}`
              },
              { role: 'user', content: userMessage }
            ],
            {
              temperature: 0.3,
              max_tokens: 150
            }
          );

          let intentText = intentAnalysis.choices[0].message.content.trim();
          intentText = intentText.replace(/```json\n?|\n?```/g, '');

          let intention;
          try {
            let cleaned = intentText
              .replace(/,(\s*[}\]])/g, '$1')
              .replace(/:\s*'([^']*)'(?=\s*[,}\]])/g, ': "$1"')
              .replace(/\/\*[\s\S]*?\*\//g, '')
              .replace(/\/\/.*/g, '')
              .trim();

            intention = JSON.parse(cleaned);
            console.log('📊 Voice memory intent:', {
              isMemoryQuery: intention.isMemoryQuery,
              confidence: intention.confidence,
              reason: intention.reason
            });
          } catch (parseError) {
            console.warn('⚠️ Intent parse failed - defaulting to NOT a memory query');
            intention = { isMemoryQuery: false, confidence: 0.5 };
          }

          if (intention.isMemoryQuery && intention.confidence > 0.4) {
            console.log('🧠 Memory query confirmed - running intelligentMemoryRecall...');

            const recall = await this.memoryService.intelligentMemoryRecall(userMessage);

            console.log('📊 Voice recall result:', {
              action: recall?.action,
              found: recall?.found,
              needsClarity: recall?.needsClarity
            });

            // ============================================
            // CASE: NEEDS CLARITY
            // ============================================
            if (recall?.needsClarity) {
              console.log('❓ Asking for clarity (voice)');

              this.messageHistory.push({ role: 'user', content: userMessage });
              this.messageHistory.push({ role: 'assistant', content: recall.clarityQuestion });

              this.onTranscriptUpdate?.({
                type: 'assistant',
                text: recall.clarityQuestion,
                isFinal: true,
                showInChat: true,
                needsFollowUp: true,
                isMemoryQuery: true
              });

              this.isAISpeaking = true;
              await this.streamAudioChunks(recall.clarityQuestion, this.selectedVoice);
              this.isAISpeaking = false;
              return;
            }

            // ============================================
            // CASE: NEEDS EXPANSION
            // ============================================
            if (recall?.needsExpansion) {
              console.log('⚠️ Need to expand search (voice)');

              const expandQuestion = `I didn't find anything from that time. Want me to search further back?`;

              this.messageHistory.push({ role: 'user', content: userMessage });
              this.messageHistory.push({ role: 'assistant', content: expandQuestion });

              this.onTranscriptUpdate?.({
                type: 'assistant',
                text: expandQuestion,
                isFinal: true,
                showInChat: true,
                needsFollowUp: true,
                expandSearch: true
              });

              this.isAISpeaking = true;
              await this.streamAudioChunks(expandQuestion, this.selectedVoice);
              this.isAISpeaking = false;
              return;
            }

            // ============================================
            // CASE: FOUND TOO MANY
            // ============================================
            if (recall?.foundTooMany) {
              console.log('⚠️ Too many results (voice)');

              const refineQuestion = recall.refinementSuggestion || `That's quite a few. Can you be more specific?`;

              this.messageHistory.push({ role: 'user', content: userMessage });
              this.messageHistory.push({ role: 'assistant', content: refineQuestion });

              this.onTranscriptUpdate?.({
                type: 'assistant',
                text: refineQuestion,
                isFinal: true,
                showInChat: true,
                needsFollowUp: true,
                refineSearch: true
              });

              this.isAISpeaking = true;
              await this.streamAudioChunks(refineQuestion, this.selectedVoice);
              this.isAISpeaking = false;
              return;
            }

            // ============================================
            // CASE: FOUND MEMORIES
            // ============================================
            if (recall?.found && recall?.response) {
              console.log('✅ Found memories (voice):', recall.totalFound);

              this.messageHistory.push({ role: 'user', content: userMessage });
              this.messageHistory.push({ role: 'assistant', content: recall.response });

              this.onTranscriptUpdate?.({
                type: 'assistant',
                text: recall.response,
                isFinal: true,
                showInChat: true,
                isMemoryRecall: true,
                memoriesCount: recall.totalFound
              });

              this.isAISpeaking = true;
              await this.streamAudioChunks(recall.response, this.selectedVoice);
              this.isAISpeaking = false;
              return;
            }

            // no_match — fall through to normal chat
            console.log('⏭️ Memory query but no match found - continuing to normal chat');

          } else {
            console.log(`⏭️ Not a memory query (confidence: ${intention?.confidence}) - normal chat`);
          }
        }

      } catch (intentError) {
        console.error('❌ Intent analysis error (voice):', intentError);
        // fall through to normal chat
      }
    }

    // ============================================
    // STEP 3: Normal conversation flow
    // ============================================
    this.messageHistory.push({
      role: 'user',
      content: userMessage
    });

    this.isAISpeaking = true;
    this.streamBuffer = '';
    let fullResponse = '';
    let firstAudioQueued = false;

    this.onTranscriptUpdate?.({
      type: 'assistant',
      text: '',
      isFinal: false,
      isLoading: true,
      showInChat: true
    });

    const reader = await this.getAzureResponseStream(userMessage);
    const decoder = new TextDecoder();

    console.log('📖 Starting stream (voice)...');

    let buffer = '';

    while (true) {
      if (!this.isAISpeaking) {
        console.log('🛑 User interrupted');
        reader.cancel();
        return;
      }

      const { done, value } = await reader.read();

      if (done) {
        console.log('✅ Stream complete');

        if (buffer.trim()) {
          const lines = buffer.split('\n');
          for (const line of lines) {
            if (!line.trim() || !line.startsWith('data: ') || line === 'data: [DONE]') continue;
            try {
              const json = JSON.parse(line.slice(6));
              const content =
                json.choices?.[0]?.delta?.content ||
                json.choices?.[0]?.message?.content || '';
              if (content) {
                fullResponse += content;
                this.streamBuffer += content;
              }
            } catch (e) {
              console.warn('⚠️ Parse error in final buffer');
            }
          }
        }
        break;
      }

      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;

      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.trim() || !line.startsWith('data: ') || line === 'data: [DONE]') continue;

        try {
          const json = JSON.parse(line.slice(6));
          let content = '';

          if (json.choices?.[0]?.delta?.content) {
            content = json.choices[0].delta.content;
          } else if (json.choices?.[0]?.message?.content) {
            content = json.choices[0].message.content;
          }

          if (!content) {
            const toolCalls =
              json.choices?.[0]?.delta?.tool_calls ||
              json.choices?.[0]?.message?.tool_calls;
            if (toolCalls) {
              console.log('🔧 Tool call detected');
              this.handleToolCallStream(toolCalls, fullResponse);
              return;
            }
            continue;
          }

          if (!this.isAISpeaking) {
            console.log('🛑 Interrupted mid-stream');
            return;
          }

          this.streamBuffer += content;
          fullResponse += content;

          this.onTranscriptUpdate?.({
            type: 'assistant',
            text: fullResponse,
            isFinal: false,
            isLoading: false,
            showInChat: true,
            isStreaming: true
          });

          // Queue first sentence for TTS
          if (this.streamBuffer.match(/[.!?]\s/) && !firstAudioQueued) {
            const sentences = this.streamBuffer.split(/([.!?])/);
            const completeSentence = sentences.slice(0, 2).join('').trim();

            if (completeSentence) {
              firstAudioQueued = true;
              if (this.isAISpeaking) {
                this.queueTTSGeneration(completeSentence).catch(e => {
                  console.error('TTS error:', e);
                });
              }
              this.streamBuffer = sentences.slice(2).join('').trim();
            }
          } else if (this.streamBuffer.match(/[.!?]\s/) && firstAudioQueued) {
            const sentences = this.streamBuffer.split(/([.!?])/);
            const sentence = sentences.slice(0, 2).join('').trim();

            if (sentence && this.isAISpeaking) {
              this.queueTTSGeneration(sentence).catch(e => {
                console.error('TTS error:', e);
              });
              this.streamBuffer = sentences.slice(2).join('').trim();
            }
          }

        } catch (parseError) {
          console.warn('⚠️ Parse error');
        }
      }
    }

    // Queue remaining text
    if (this.streamBuffer.trim() && this.isAISpeaking) {
      this.queueTTSGeneration(this.streamBuffer).catch(e => {
        console.error('TTS error:', e);
      });
    }

    if (!fullResponse.trim()) {
      fullResponse = "Sorry, didn't catch that. Could you repeat that?";
      if (this.isAISpeaking) {
        this.queueTTSGeneration(fullResponse).catch(e => {
          console.error('TTS error:', e);
        });
      }
    }

    this.messageHistory.push({
      role: 'assistant',
      content: fullResponse
    });

    this.onTranscriptUpdate?.({
      type: 'assistant',
      text: fullResponse,
      isFinal: true,
      isLoading: false,
      showInChat: true
    });

    // ============================================
    // STEP 4: Follow-up check (background, non-blocking)
    // ============================================
    if (this.analyzeFollowUp && this.memoryService?.isReady?.()) {
      setTimeout(async () => {
        try {
          console.log('🧠 Analyzing follow-up (voice)...');

          const chatMessages = this.messageHistory.map((msg, idx) => ({
            id: Date.now() + idx,
            type: msg.role === 'user' ? 'user' : 'bot',
            content: msg.content || '',
            timestamp: new Date().toISOString()
          }));

          let relevantMemories = [];
          try {
            relevantMemories = await this.memoryService.getRecentMemories(20, 30);
          } catch (e) {
            console.warn('⚠️ Memory load for follow-up failed');
          }

          const analysis = await this.analyzeFollowUp(
            this.userName,
            userMessage,
            chatMessages,
            relevantMemories,
            fullResponse
          );

          if (analysis.shouldFollowUp && analysis.followUpText) {
            console.log('✅ Follow-up approved (voice):', analysis.followUpText);

            await new Promise(resolve =>
              setTimeout(resolve, 2000 + Math.random() * 1000)
            );

            this.messageHistory.push({
              role: 'assistant',
              content: analysis.followUpText
            });

            this.onTranscriptUpdate?.({
              type: 'assistant',
              text: analysis.followUpText,
              isFinal: true,
              showInChat: true,
              isFollowUp: true
            });

            this.isAISpeaking = true;
            await this.streamAudioChunks(analysis.followUpText, this.selectedVoice);
            this.isAISpeaking = false;
          }
        } catch (error) {
          console.warn('⚠️ Follow-up error (voice):', error);
        }
      }, 1500);
    }

    console.log('✅ Voice response done');

  } catch (error) {
    console.error('❌ generateAndStreamResponse error:', error);
    this.isAISpeaking = false;

    this.onTranscriptUpdate?.({
      type: 'assistant',
      text: "Something went wrong. Try again.",
      isFinal: true,
      showInChat: true
    });
  }
}

async queueTTSGeneration(text) {
  this.ttsQueue.push(text);
  this.isAISpeaking = true;  // ✅ AI is now speaking
  
  if (!this.isTTSProcessing) {
    this.processTTSQueue();
  }
}
  async processTTSQueue() {
    while (this.ttsQueue.length > 0) {
      this.isTTSProcessing = true;
      const text = this.ttsQueue.shift();

      try {
        const audioBlob = await this.convertTextToSpeechAzure(text);
        if (audioBlob) {
          console.log('🎵 Audio ready, queueing for playback');
          this.queueAudioForPlayback(audioBlob);
        }
      } catch (error) {
        console.error('❌ TTS conversion error:', error);
      }
    }
    this.isTTSProcessing = false;
  }

async handleToolCallStream(toolCalls, fullResponse) {
  console.log('🔧 Handling tool call from stream');
  
  const toolCall = toolCalls[0];
  if (!toolCall) return;

  const toolCallId = toolCall.id;
  const functionName = toolCall.function?.name;
  const functionArgs = toolCall.function?.arguments 
    ? (typeof toolCall.function.arguments === 'string' 
        ? JSON.parse(toolCall.function.arguments)
        : toolCall.function.arguments)
    : {};

  console.log('🔧 Tool Call Details:', {
    id: toolCallId,
    function: functionName,
    args: functionArgs
  });

  // ✅ ADD THIS: Track in message history BEFORE processing
  this.messageHistory.push({
    role: 'assistant',
    content: fullResponse || '',
    tool_calls: [{
      id: toolCallId,
      function: { 
        name: functionName, 
        arguments: JSON.stringify(functionArgs) 
      }
    }]
  });

  this.isAISpeaking = false;

  // ✅ CRITICAL: Call the UI's tool handler (same as chat mode)
  if (this.onToolCall) {
    console.log('📞 Triggering onToolCall callback for:', functionName);
    
    this.onToolCall({
      toolCallId,
      functionName,
      arguments: functionArgs,
      messageHistory: this.messageHistory
    });
    
    // ✅ Mark as processed
    this.lastToolCallTime = Date.now();
  } else {
    console.warn('⚠️ No onToolCall handler registered!');
  }
}

  async streamAudioChunks(fullText) {
    try {
      const chunks = this.splitIntoChunks(fullText, 15);
      console.log(`🎵 Queuing ${chunks.length} chunks`);

      for (const chunk of chunks) {
        if (!chunk.trim()) continue;
        
        const audioBlob = await this.convertTextToSpeechAzure(chunk);
        if (audioBlob) {
          this.queueAudioForPlayback(audioBlob);
        }

        await new Promise(resolve => setTimeout(resolve, 50));
      }

      console.log('✅ All chunks queued');
    } catch (error) {
      console.error('❌ Stream error:', error);
    }
  }

  splitIntoChunks(text, wordsPerChunk = 15) {
    const sentences = text.match(/[^.!?]+[.!?]+/g);

    if (sentences && sentences.length > 0) {
      return sentences.map(s => s.trim());
    }

    const words = text.split(/\s+/);
    const chunks = [];

    for (let i = 0; i < words.length; i += wordsPerChunk) {
      chunks.push(words.slice(i, i + wordsPerChunk).join(' '));
    }

    return chunks;
  }
async convertTextToSpeechAzure(text) {
  return new Promise(async (resolve, reject) => {
    try {
      await this.refreshTokenIfNeeded();

      if (!this.synthesizer) {
        console.warn('⚠️ Synthesizer not ready, attempting to initialize...');
        
        const { token, region } = await this.getAuthToken();
        
        this.speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(
          token,
          region
        );
        
        this.speechConfig.speechRecognitionLanguage = 'en-US';
        this.speechConfig.speechSynthesisVoiceName = this.selectedVoice;

        this.synthesizer = new SpeechSDK.SpeechSynthesizer(this.speechConfig, null);
        console.log('✅ Synthesizer initialized on-demand with token auth');
      }

      const ssml = `
        <speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xml:lang="en-US">
          <voice name="${this.selectedVoice}">
            <prosody rate="${this.audioSpeed}" pitch="${this.audioPitch > 1 ? '+' : ''}${((this.audioPitch - 1) * 50).toFixed(0)}%">
              ${this.escapeXml(text)}
            </prosody>
          </voice>
        </speak>
      `.trim();

      console.log('🎵 Generating Azure TTS...');

      this.synthesizer.speakSsmlAsync(
        ssml,
        (result) => {
          if (result.reason === SpeechSDK.ResultReason.SynthesizingAudioCompleted) {
            console.log('✅ TTS generated successfully');
            const audioBlob = new Blob([result.audioData], { type: 'audio/wav' });
            resolve(audioBlob);
          } else {
            console.error('❌ TTS failed:', result.errorDetails);
            reject(new Error(result.errorDetails));
          }
        },
        (error) => {
          console.error('❌ TTS error:', error);
          reject(error);
        }
      );
    } catch (error) {
      console.error('❌ TTS conversion error:', error);
      reject(error);
    }
  });
}

  escapeXml(text) {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }

queueAudioForPlayback(audioBlob) {
  this.audioQueue.push(audioBlob);

  if (!this.isPlayingAudio) {
    this.isPlayingAudio = true;  // ✅ SET THIS FIRST
    this.isAISpeaking = true;    // ✅ SET THIS TOO
    this.playNextAudio();
  }
}
playNextAudio() {
  // ✅ Check interruption for BOTH voice AND text interruptions
  if (!this.isAISpeaking && this.audioQueue.length > 0) {
    console.log('🛑 User interrupted (voice or text) - clearing audio queue');
    this.audioQueue = [];
    this.isPlayingAudio = false;
    this.currentAudioElement = null;
    return;
  }
  if (this.audioQueue.length === 0) {
    this.isPlayingAudio = false;
    this.currentAudioElement = null;
    console.log('✅ All audio played');
    this.isAISpeaking = false;
    return;
  }

  this.isPlayingAudio = true;

  const audioBlob = this.audioQueue.shift();
  const audioUrl = URL.createObjectURL(audioBlob);
  const audio = new Audio(audioUrl);

  this.currentAudioElement = audio;
  audio.volume = 1.0;

  audio.onended = () => {
    URL.revokeObjectURL(audioUrl);
    this.currentAudioElement = null;
    
    // ✅ Continue playing remaining chunks
    this.playNextAudio();
  };

  audio.onerror = (error) => {
    console.error('❌ Playback error:', error);
    URL.revokeObjectURL(audioUrl);
    this.currentAudioElement = null;
    this.playNextAudio();
  };

  console.log('▶️ Playing chunk');
  audio.play().catch(error => {
    console.error('❌ Play error:', error);
    URL.revokeObjectURL(audioUrl);
    this.playNextAudio();
  });
}
interruptCurrentAudio() {
  console.log('⏸️ INTERRUPTING AI AUDIO (User speaking)');

  // Stop current audio immediately
  if (this.currentAudioElement) {
    console.log('⏸️ Stopping current audio chunk');
    this.currentAudioElement.pause();
    this.currentAudioElement.currentTime = 0;
    this.currentAudioElement = null;
  }

  // Clear all queued audio chunks
  if (this.audioQueue.length > 0) {
    console.log(`🗑️ Clearing ${this.audioQueue.length} queued chunks`);
    this.audioQueue = [];
  }

  // Stop TTS generation
  this.isTTSProcessing = false;
  this.ttsQueue = [];
  
  // Reset audio state
  this.isPlayingAudio = false;
  this.isAISpeaking = false;

  console.log('✅ Audio fully interrupted - ready for user input');
}

  toggleMicrophone(shouldMute) {
    if (!this.recognizer) {
      console.warn('⚠️ Recognizer not ready');
      return;
    }

    this.isMuted = shouldMute;

    if (shouldMute) {
      this.recognizer.stopContinuousRecognitionAsync(
        () => {
          console.log('🔇 Muted');
          this.isListening = false;
          this.onWaveformUpdate?.({
            points: '',
            isActive: false,
            height: 0
          });
        },
        (error) => {
          console.error('❌ Mute error:', error);
        }
      );
    } else {
      this.recognizer.startContinuousRecognitionAsync(
        () => {
          console.log('🎤 Unmuted');
          this.isListening = true;
          this.animateWaveform();
        },
        (error) => {
          console.error('❌ Unmute error:', error);
        }
      );
    }
  }

  setVoice(voiceName) {
    this.selectedVoice = voiceName;
    if (this.speechConfig) {
      this.speechConfig.speechSynthesisVoiceName = voiceName;
      console.log(`🎵 Voice changed to: ${voiceName}`);
    }
  }

  setAudioSpeed(speed) {
    this.audioSpeed = Math.max(0.5, Math.min(2.0, speed));
    console.log(`⚡ Speed: ${this.audioSpeed}x`);
  }

  setAudioPitch(pitch) {
    this.audioPitch = Math.max(0.5, Math.min(2.0, pitch));
    console.log(`🎼 Pitch: ${this.audioPitch}x`);
  }

/**
 * ✅ Load memories and goals into voice mode context
 */

async loadMemoriesForContext() {
  try {
    console.log('🧠 Loading memories for voice mode...');

    // ============================================
    // STEP 1: Validate memory service
    // ============================================
    if (!this.memoryService) {
      console.warn('⚠️ Memory service not provided to VoiceRealtimeManager');
      this.memoriesShortTerm = [];
      this.memoriesLongTerm = [];
      return;
    }

    // ============================================
    // STEP 2: Initialize memory service
    // ============================================
    if (!this.memoryService.isReady?.()) {
      console.log('⏳ Memory service not ready - initializing...');
      
      try {
        await this.memoryService.initialize();
        console.log('✅ Memory service initialized');
      } catch (initError) {
        console.error('❌ Memory service init failed:', initError.message);
        this.memoriesShortTerm = [];
        this.memoriesLongTerm = [];
        return; // Exit gracefully
      }
    }

    // ============================================
    // STEP 3: Load short-term memories
    // ============================================
    console.log('📌 Fetching short-term memories...');
    
    try {
      // Try multiple method names since implementations vary
      let shortTerm = [];
      
      if (typeof this.memoryService.getShortTermMemories === 'function') {
        shortTerm = await this.memoryService.getShortTermMemories(10);
      } else if (typeof this.memoryService.getRecentMemories === 'function') {
        shortTerm = await this.memoryService.getRecentMemories(10);
      } else if (typeof this.memoryService.getMemories === 'function') {
        shortTerm = await this.memoryService.getMemories({ 
          type: 'short-term',
          limit: 10 
        });
      } else {
        console.warn('⚠️ No short-term memory method found');
        shortTerm = [];
      }

      this.memoriesShortTerm = shortTerm || [];
      console.log(`✅ Short-term memories: ${this.memoriesShortTerm.length} loaded`);
      
      if (this.memoriesShortTerm.length > 0) {
        console.log('   Memories:', this.memoriesShortTerm.map(m => m.memory || m.content).slice(0, 3));
      }
      
    } catch (shortTermError) {
      console.error('❌ Failed to load short-term memories:', shortTermError.message);
      this.memoriesShortTerm = [];
    }

    // ============================================
    // STEP 4: Load long-term memories
    // ============================================
    console.log('📚 Fetching long-term memories...');
    
    try {
      // Try multiple method names
      let longTerm = [];
      
      if (typeof this.memoryService.getLongTermMemories === 'function') {
        longTerm = await this.memoryService.getLongTermMemories(20);
      } else if (typeof this.memoryService.getPermanentMemories === 'function') {
        longTerm = await this.memoryService.getPermanentMemories(20);
      } else if (typeof this.memoryService.getMemories === 'function') {
        longTerm = await this.memoryService.getMemories({ 
          type: 'long-term',
          limit: 20 
        });
      } else {
        console.warn('⚠️ No long-term memory method found');
        longTerm = [];
      }

      this.memoriesLongTerm = longTerm || [];
      console.log(`✅ Long-term memories: ${this.memoriesLongTerm.length} loaded`);
      
      if (this.memoriesLongTerm.length > 0) {
        console.log('   Memories:', this.memoriesLongTerm.map(m => m.memory || m.content).slice(0, 3));
      }
      
    } catch (longTermError) {
      console.error('❌ Failed to load long-term memories:', longTermError.message);
      this.memoriesLongTerm = [];
    }

    // ============================================
    // STEP 5: Try to load memory summary for AI
    // ============================================
    console.log('📝 Loading memory summary for AI...');
    
    try {
      if (this.memoryService?.getMemorySummaryForAI) {
        const memorySummary = await this.memoryService.getMemorySummaryForAI('');
        if (memorySummary) {
          this.memoryContextSummary = memorySummary;
          console.log('✅ Memory summary prepared');
        }
      }
    } catch (summaryError) {
      console.warn('⚠️ Memory summary not available:', summaryError.message);
    }

    // ============================================
    // STEP 6: Load goals (if available)
    // ============================================
    console.log('🎯 Loading goals...');
    
    try {
      if (window.goalService && window.user?.uid) {
        const allGoals = await window.goalService.getAllGoals(window.user.uid);
        
        if (Array.isArray(allGoals) && allGoals.length > 0) {
          this.goalsShortTerm = allGoals.filter(goal => {
            try {
              const daysUntilDue = window.goalService.calculateDaysUntilDue(goal);
              return daysUntilDue >= 0 && daysUntilDue <= 7;
            } catch (e) {
              return false;
            }
          });

          this.goalsMediumTerm = allGoals.filter(goal => {
            try {
              const daysUntilDue = window.goalService.calculateDaysUntilDue(goal);
              return daysUntilDue > 7 && daysUntilDue <= 90;
            } catch (e) {
              return false;
            }
          });

          this.goalsLongTerm = allGoals.filter(goal => {
            try {
              const daysUntilDue = window.goalService.calculateDaysUntilDue(goal);
              return daysUntilDue > 90;
            } catch (e) {
              return false;
            }
          });

          console.log(`✅ Goals loaded: ${allGoals.length} total`);
        } else {
          console.log('📭 No goals found');
          this.goalsShortTerm = [];
          this.goalsMediumTerm = [];
          this.goalsLongTerm = [];
        }
      }
    } catch (goalsError) {
      console.warn('⚠️ Goals not available:', goalsError.message);
      this.goalsShortTerm = [];
      this.goalsMediumTerm = [];
      this.goalsLongTerm = [];
    }

    // ============================================
    // FINAL: Log what we got
    // ============================================
    this.lastMemoryLoad = Date.now();

    console.log('✅ ==========================================');
    console.log('   MEMORY LOAD COMPLETE');
    console.log('✅ ==========================================');
    console.log(`   📌 Short-term: ${this.memoriesShortTerm.length}`);
    console.log(`   📚 Long-term: ${this.memoriesLongTerm.length}`);
    console.log(`   ⚡ Short-term goals: ${this.goalsShortTerm?.length || 0}`);
    console.log(`   📆 Medium-term goals: ${this.goalsMediumTerm?.length || 0}`);
    console.log(`   🔄 Long-term goals: ${this.goalsLongTerm?.length || 0}`);
    console.log('✅ ==========================================');

    return true;

  } catch (outerError) {
    console.error('❌ CRITICAL: Memory load failed:', outerError.message);
    
    // Set safe defaults
    this.memoriesShortTerm = [];
    this.memoriesLongTerm = [];
    this.goalsShortTerm = [];
    this.goalsMediumTerm = [];
    this.goalsLongTerm = [];
    
    return false; // Indicate failure but don't crash
  }
}
disconnect() {
  console.log('🧹 Cleaning up voice mode...');

  // ✅ Save conversation summary (uses chat's same function)
  if (this.messageHistory.length > 0 && this.memoryService?.isReady?.()) {
    console.log('📝 Saving voice conversation summary...');
    
    const chatMessages = this.messageHistory.map((msg, idx) => ({
      id: Date.now() + idx,
      type: msg.role === 'user' ? 'user' : 'bot',
      content: msg.content || '',
      timestamp: new Date().toISOString()
    }));

    // ✅ REMOVED: azureConfig - saveConversationSummary uses SecureAPI internally
    this.memoryService.saveConversationSummary(chatMessages)
      .then(() => console.log('✅ Voice conversation summary saved'))
      .catch(e => console.error('⚠️ Failed to save summary:', e));
  }

  // Stop recognition
  if (this.recognizer) {
    this.recognizer.stopContinuousRecognitionAsync();
    this.recognizer.close();
    this.recognizer = null;
  }

  // Stop synthesis
  if (this.synthesizer) {
    this.synthesizer.close();
    this.synthesizer = null;
  }

  // Interrupt any playing audio
  this.interruptCurrentAudio();

  // Cancel animation frame
  if (this.animationFrameId) {
    cancelAnimationFrame(this.animationFrameId);
    this.animationFrameId = null;
  }

  // Close audio context
  if (this.audioContext && this.audioContext.state !== 'closed') {
    this.audioContext.close();
    this.audioContext = null;
  }

  // Stop audio stream
  if (this.audioStream) {
    this.audioStream.getTracks().forEach(track => track.stop());
    this.audioStream = null;
  }

  // Clear timers
  if (this.silenceTimer) {
    clearTimeout(this.silenceTimer);
    this.silenceTimer = null;
  }

  // Reset all state flags
  this.isConnected = false;
  this.isListening = false;
  this.isInitializing = false;
  this.isMuted = false;
  this.isPlayingAudio = false;
  this.isAISpeaking = false;
  this.isUserSpeaking = false;
  this.isTTSProcessing = false;

  // Clear queues
  this.audioQueue = [];
  this.ttsQueue = [];
  this.messageHistory = [];
  this.streamBuffer = '';
  this.currentAudioElement = null;
  this.currentWaveformData = null;

  // Clear memory context
  this.memoriesShortTerm = [];
  this.memoriesLongTerm = [];
  this.goalsShortTerm = [];
  this.goalsMediumTerm = [];
  this.goalsLongTerm = [];
  this.lastMemoryLoad = null;

  console.log('✅ Voice mode fully disconnected and cleaned up');
}
}

export default VoiceRealtimeManager;